<script setup lang="ts">
/**
 * «Начало работы · 2 из 4» — макет 25:2: карточка с изумрудной полосой и
 * четырьмя строками, сделанные гаснут.
 *
 * Строка — ссылка на то место, где шаг делается: чек-лист, который только
 * отмечает, но никуда не ведёт, заставляет искать экран самому.
 */
import { Check } from 'lucide-vue-next'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { doneCount, type Step } from './api'

const props = defineProps<{ steps: Step[]; projectKey: string }>()

const emit = defineEmits<{ 'create-project': [] }>()

const { t } = useI18n()

const done = computed(() => doneCount(props.steps))

/** Куда ведёт строка: без проекта вести некуда, кроме как заводить его. */
const routeOf = (step: Step): string => {
  if (!props.projectKey) return ''

  if (step.key === 'issue') return `/p/${props.projectKey}/board`
  if (step.key === 'columns') return `/p/${props.projectKey}/settings?tab=columns`
  if (step.key === 'team') return `/p/${props.projectKey}/settings?tab=members`

  return ''
}
</script>

<template>
  <section class="onb">
    <h2 class="onb__title">{{ t('onboarding.title', { done, total: steps.length }) }}</h2>

    <div class="onb__track">
      <span class="onb__fill" :style="{ width: `${(done / steps.length) * 100}%` }" />
    </div>

    <ul class="onb__list">
      <li v-for="step in steps" :key="step.key" class="onb__item">
        <span class="onb__mark" :class="{ 'onb__mark--done': step.done }">
          <Check v-if="step.done" :size="10" :stroke-width="3" />
        </span>

        <RouterLink v-if="!step.done && routeOf(step)" class="onb__link" :to="routeOf(step)">
          {{ t(`onboarding.steps.${step.key}`) }}
        </RouterLink>

        <button
          v-else-if="!step.done"
          type="button"
          class="onb__link onb__link--button"
          @click="emit('create-project')"
        >
          {{ t(`onboarding.steps.${step.key}`) }}
        </button>

        <span v-else class="onb__text onb__text--done">
          {{ t(`onboarding.steps.${step.key}`) }}
        </span>
      </li>
    </ul>
  </section>
</template>

<style scoped>
.onb {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 440px;
  max-width: 100%;
  padding: 16px 20px 18px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  background: var(--color-surface);
}

.onb__title {
  margin: 0;
  color: var(--color-text-primary);
  font-size: var(--font-size-body);
  font-weight: var(--font-weight-semibold);
}

.onb__track {
  height: 4px;
  border-radius: var(--radius-sm);
  background: var(--color-bg-muted);
}

.onb__fill {
  display: block;
  height: 100%;
  border-radius: var(--radius-sm);
  background: var(--color-success);
  transition: width 0.2s ease;
}

.onb__list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.onb__item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.onb__mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  border: 1px solid var(--color-border-strong, var(--color-border));
  border-radius: 50%;
  color: var(--color-text-inverse, #fff);
}

.onb__mark--done {
  border-color: var(--color-success);
  background: var(--color-success);
}

.onb__link,
.onb__text {
  color: var(--color-text-primary);
  font-size: var(--font-size-body-sm);
  text-decoration: none;
}

.onb__link:hover {
  color: var(--color-primary);
}

.onb__link--button {
  padding: 0;
  border: none;
  background: none;
  font-family: var(--font-family-sans);
  cursor: pointer;
}

/* Сделанный шаг гаснет: он остаётся в списке как след пути, но внимания больше
   не просит. */
.onb__text--done {
  color: var(--color-text-muted);
}
</style>
